import { useState } from "react";
import { ScrollText, Search } from "lucide-react";
import { MasterShell } from "@/shared/layouts/MasterShell.jsx";
import { Card } from "@/shared/ui/card.jsx";
import { Badge } from "@/shared/ui/badge.jsx";
import { Input } from "@/shared/ui/input.jsx";
import { Spinner, EmptyState } from "@/shared/ui/spinner.jsx";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/shared/ui/table.jsx";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/shared/ui/select.jsx";
import { useDebounced } from "@/shared/hooks/use-debounced.js";
import { StatCard } from "./components/MasterUi.jsx";
import { useMasterAudit } from "./use-master.js";

/**
 * Trilha de auditoria.
 *
 * Os registros são gravados pelos próprios services quando algo sensível
 * acontece: login, mudança de etapa de lead, alteração de credenciais,
 * exclusão, entrada de um Master no ambiente de um cliente.
 */

const CORES = {
  Login: "secondary",
  Alteração: "default",
  Integração: "warning",
  Exclusão: "destructive",
  "Acesso Master": "outline",
};

export function AuditPage() {
  const [categoria, setCategoria] = useState("todas");
  const [busca, setBusca] = useState("");
  const buscaAdiada = useDebounced(busca, 300);

  const { data, isLoading } = useMasterAudit({
    category: categoria,
    search: buscaAdiada,
    limit: 200,
  });

  const registros = data?.logs ?? [];
  const resumo = data?.summary;
  const categorias = data?.categories ?? [];

  return (
    <MasterShell title="Auditoria" subtitle="Registro das ações sensíveis na plataforma">
      <div className="space-y-4 p-6">
        {resumo && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <StatCard label="Total de eventos" value={resumo.total} accent="primary" />
            {categorias.map((cat) => (
              <StatCard key={cat} label={cat} value={resumo.porCategoria?.[cat] ?? 0} accent="chart-2" />
            ))}
          </div>
        )}

        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-60 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por usuário ou ação..."
                className="pl-9"
                aria-label="Buscar na auditoria"
              />
            </div>

            <Select value={categoria} onValueChange={setCategoria}>
              <SelectTrigger className="w-52">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas as categorias</SelectItem>
                {categorias.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Card>

        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-t-0 hover:bg-transparent">
                <TableHead className="w-28">Data</TableHead>
                <TableHead className="w-20">Hora</TableHead>
                <TableHead>Usuário</TableHead>
                <TableHead>Ação</TableHead>
                <TableHead className="w-40">Categoria</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center">
                    <Spinner className="mx-auto h-6 w-6 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : registros.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <EmptyState
                      icon={ScrollText}
                      title="Nenhum registro encontrado"
                      description="Ajuste os filtros para ver outros eventos."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                registros.map((registro) => (
                  <TableRow key={registro.id}>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {registro.date}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {registro.time}
                    </TableCell>
                    <TableCell className="text-sm font-medium">{registro.user}</TableCell>
                    <TableCell className="text-sm">{registro.action}</TableCell>
                    <TableCell>
                      <Badge variant={CORES[registro.category] ?? "outline"} className="text-[10px]">
                        {registro.category}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </MasterShell>
  );
}
